import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Switch } from 'components';
import { useAnalytics } from 'analytics';
import { ANALYTICS_EVENTS } from 'analytics/events';
import { useAudioDevice } from 'providers/AudioDeviceProvider';
import { useAudioDevicePreview } from 'hooks/useAudioDevicePreview';
import { displayDeviceLabel } from 'utils/audio/displayDeviceLabel';
import { findStoredDevice } from 'utils/audio/findStoredDevice';
import { matchesRecommendedDevice } from 'utils/audio/recommendedDevice';
import type { AudioInputDevice } from 'utils/audio/audioDeviceTypes';
import { LevelMeter } from './LevelMeter';
import {
  DeviceOption,
  DeviceSelect,
  DeviceSettingsAccordion,
  DeviceSettingsContent,
  DeviceSettingsItem,
  DeviceSettingsRows,
  DeviceSettingsTrigger,
  FieldLabel,
  NotifyLabel,
  NotifyRow,
  SaveButton,
  SettingsTestToneButton,
  SpeakerFieldLabel,
} from './DeviceSettingsSection.styles';

const ITEM_VALUE = 'device-settings';
const UNAVAILABLE_VALUE = 'unavailable';

const isRecommended = (devices: AudioInputDevice[], deviceId: string) =>
  devices.some((device) => device.deviceId === deviceId && matchesRecommendedDevice(device.label));

export const DeviceSettingsSection = () => {
  const { t } = useTranslation();
  const { trackEvent } = useAnalytics();
  const audio = useAudioDevice();
  const [open, setOpen] = useState(audio.state !== 'recommendedConnected');
  const [chosenDeviceId, setChosenDeviceId] = useState<string | null>(null);
  const [chosenSpeakerId, setChosenSpeakerId] = useState<string | null>(null);
  const [chosenNotify, setChosenNotify] = useState<boolean | null>(null);
  const [saving, setSaving] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);

  // A connected Jabra needs no attention here, everything else does.
  useEffect(() => {
    setOpen(audio.state !== 'recommendedConnected');
  }, [audio.state]);

  useEffect(() => {
    const listed = audio.devices.some((device) => device.deviceId === chosenDeviceId);
    if (chosenDeviceId !== null && !listed) setChosenDeviceId(null);
  }, [audio.devices, chosenDeviceId]);

  useEffect(() => {
    const listed = audio.outputs.some((output) => output.deviceId === chosenSpeakerId);
    if (chosenSpeakerId !== null && !listed) setChosenSpeakerId(null);
  }, [audio.outputs, chosenSpeakerId]);

  const currentDeviceId = audio.selectedDevice?.deviceId ?? '';
  const deviceId = chosenDeviceId ?? currentDeviceId;
  const currentSpeakerId = audio.selectedSpeaker?.deviceId ?? '';
  const speakerId = chosenSpeakerId ?? currentSpeakerId;
  const notify = chosenNotify ?? audio.settings.notifyWhenRecommendedAvailable;
  const dirty =
    deviceId !== currentDeviceId ||
    speakerId !== currentSpeakerId ||
    notify !== audio.settings.notifyWhenRecommendedAvailable;

  const level = useAudioDevicePreview(open && deviceId ? deviceId : null);

  const recommendedSuffix = ` · ${t('audio.settings.badges.recommended')}`;
  const unavailableSuffix = ` · ${t('audio.settings.badges.unavailable')}`;
  const storedRecommended = audio.settings.recommendedMicrophone;
  const storedSpeaker = audio.settings.confirmedSpeaker;
  const storedSpeakerMissing =
    storedSpeaker !== null && findStoredDevice(audio.outputs, storedSpeaker) === undefined;

  const save = async () => {
    setSaving(true);
    try {
      await audio.chooseDevices({
        microphoneId: chosenDeviceId,
        speakerId: chosenSpeakerId,
        notifyWhenRecommendedAvailable: notify,
      });
      setChosenDeviceId(null);
      setChosenSpeakerId(null);
      setChosenNotify(null);
      trackEvent(ANALYTICS_EVENTS.AUDIO_SETTINGS_SAVED, {
        isRecommended: isRecommended(audio.devices, deviceId),
        speakerIsRecommended: isRecommended(audio.outputs, speakerId),
      });
      toast.success(t('audio.settings.saved'));
    } catch {
      toast.error(t('audio.settings.saveFailed'));
    } finally {
      setSaving(false);
      triggerRef.current?.focus();
    }
  };

  return (
    <DeviceSettingsAccordion
      type="single"
      collapsible
      value={open ? ITEM_VALUE : ''}
      onValueChange={(value) => setOpen(value === ITEM_VALUE)}
    >
      <DeviceSettingsItem value={ITEM_VALUE}>
        <DeviceSettingsTrigger ref={triggerRef} $open={open}>
          {t('audio.settings.deviceSettings')}
        </DeviceSettingsTrigger>
        <DeviceSettingsContent>
          <DeviceSettingsRows>
            <FieldLabel htmlFor="microphone-select">{t('audio.settings.microphone')}</FieldLabel>
            <DeviceSelect
              id="microphone-select"
              value={deviceId}
              onChange={(event) => setChosenDeviceId(event.target.value)}
              $empty={deviceId === ''}
            >
              <option value="" disabled>
                {t('audio.settings.chooseDevice')}
              </option>
              {storedRecommended && audio.recommendedCandidate === null && (
                <DeviceOption value={UNAVAILABLE_VALUE} disabled>
                  {`${displayDeviceLabel(storedRecommended.label)}${recommendedSuffix}${unavailableSuffix}`}
                </DeviceOption>
              )}
              {audio.devices.map((device) => (
                <DeviceOption key={device.deviceId} value={device.deviceId}>
                  {displayDeviceLabel(device.label)}
                  {matchesRecommendedDevice(device.label) ? recommendedSuffix : ''}
                </DeviceOption>
              ))}
            </DeviceSelect>
            <LevelMeter level={level} label={t('audio.settings.levelChosen')} />

            <SpeakerFieldLabel htmlFor="speaker-select">
              {t('audio.settings.speaker')}
            </SpeakerFieldLabel>
            <DeviceSelect
              id="speaker-select"
              value={speakerId}
              onChange={(event) => setChosenSpeakerId(event.target.value)}
              disabled={audio.outputs.length === 0}
              $empty={speakerId === ''}
            >
              <option value="" disabled>
                {audio.outputs.length === 0
                  ? t('audio.settings.noSpeaker')
                  : t('audio.settings.chooseDevice')}
              </option>
              {storedSpeaker && storedSpeakerMissing && (
                <DeviceOption value={UNAVAILABLE_VALUE} disabled>
                  {`${displayDeviceLabel(storedSpeaker.label)}${unavailableSuffix}`}
                </DeviceOption>
              )}
              {audio.outputs.map((output) => (
                <DeviceOption key={output.deviceId} value={output.deviceId}>
                  {displayDeviceLabel(output.label)}
                  {matchesRecommendedDevice(output.label) ? recommendedSuffix : ''}
                </DeviceOption>
              ))}
            </DeviceSelect>
            <SettingsTestToneButton
              sinkId={speakerId === '' ? undefined : speakerId}
              source="settings"
              name={t('audio.settings.playTestToneChosen')}
            />
          </DeviceSettingsRows>

          <NotifyRow>
            <Switch
              id="notify-recommended-available"
              checked={notify}
              onCheckedChange={(value: boolean) => setChosenNotify(value)}
            />
            <NotifyLabel htmlFor="notify-recommended-available">
              {t('audio.settings.notify')}
            </NotifyLabel>
          </NotifyRow>

          <SaveButton disabled={!dirty || saving} onClick={() => void save()}>
            {t('audio.settings.save')}
          </SaveButton>
        </DeviceSettingsContent>
      </DeviceSettingsItem>
    </DeviceSettingsAccordion>
  );
};
